/*
 * Versandkosten als Betrag in Drawer und Warenkorb-Seite.
 *
 * Liquid kennt keine Versandtarife, also fragt <shipping-estimate> die Raten
 * fuers Lieferland ueber /cart/shipping_rates.json ab und zeigt die
 * guenstigste: den Betrag, oder "Kostenlos", wenn eine Gratisrate greift.
 * Shopify rechnet dabei mit dem echten Warenkorb (Gewicht, Summe, Waehrung),
 * der Wert stimmt also mit dem Checkout ueberein. Schlaegt die Abfrage fehl,
 * bleibt der Text aus Liquid stehen ("Wird im Checkout berechnet").
 *
 * Markup: snippets/cart-shipping-estimate.liquid.
 */
(function () {
  /*
   * Die Tarif-API verlangt fuer diese Laender eine Postleitzahl. Die
   * Versandzonen gelten jeweils fuers ganze Land, welche es ist, spielt keine
   * Rolle. Fuer andere Laender geht die Anfrage ohne raus.
   */
  const SAMPLE_ZIPS = { DE: '10115', AT: '1010', CH: '8001', LI: '9490' };

  const STORAGE_KEY = 'theme:shipping-estimate';
  const pending = new Map();

  /*
   * Zuletzt gezeigter Wert pro Land. Nach einer Mengenaenderung setzt das
   * Nachrendern ein frisches Element ein; bis die neue Rate da ist, steht der
   * alte Betrag abgeblendet drin, statt kurz auf "Wird im Checkout berechnet"
   * zurueckzuspringen.
   */
  const lastShown = new Map();

  function readCache(key) {
    try {
      const cache = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      return Object.prototype.hasOwnProperty.call(cache, key) ? cache[key] : undefined;
    } catch (error) {
      return undefined;
    }
  }

  function writeCache(key, cents) {
    try {
      let cache = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      if (Object.keys(cache).length > 30) cache = {};
      cache[key] = cents;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
    } catch (error) {
      // Privater Modus o. ae.: dann eben ohne Cache.
    }
  }

  function fetchCheapestRate(country, currency) {
    const params = new URLSearchParams();
    params.set('shipping_address[country]', country);
    if (SAMPLE_ZIPS[country]) params.set('shipping_address[zip]', SAMPLE_ZIPS[country]);

    // Doppelten Slash vermeiden, wenn die Route ein Sprachpraefix traegt.
    const url = `${theme.routes.cart_url}/shipping_rates.json?${params}`.replace('//', '/');

    return fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const rates = data && data.shipping_rates;
        if (!Array.isArray(rates) || rates.length === 0) return null;
        // Kaeme eine Rate in einer anderen Waehrung als der Warenkorb zurueck,
        // stuende sie mit dem falschen Symbol da. Dann lieber der Liquid-Text.
        if (currency && rates.some((rate) => rate.currency && rate.currency !== currency)) return null;
        const prices = rates
          .map((rate) => Math.round(parseFloat(rate.price) * 100))
          .filter((cents) => !isNaN(cents));
        return prices.length ? Math.min(...prices) : null;
      })
      .catch(() => null);
  }

  function isCurrent(key) {
    return Array.from(document.querySelectorAll('shipping-estimate')).some((el) => el.dataset.key === key);
  }

  function cheapestRate(key, country, currency) {
    const cached = readCache(key);
    if (cached !== undefined) return Promise.resolve(cached);

    if (!pending.has(key)) {
      pending.set(
        key,
        fetchCheapestRate(country, currency).then((cents) => {
          pending.delete(key);
          // Shopify rechnet mit dem Warenkorb von jetzt, nicht dem beim Rendern.
          // Hat er sich inzwischen geaendert (kein Element mit diesem Schluessel
          // mehr da), gehoert die Antwort nicht unter diesen Schluessel.
          if (cents !== null && isCurrent(key)) writeCache(key, cents);
          return cents;
        })
      );
    }
    return pending.get(key);
  }

  /*
   * Formatiert wie Liquids `money` im aktuellen Markt. Liquid gibt dafuer
   * 1000,00 in der Waehrung des Warenkorbs mit ("1.000,00 €", "CHF 1'000.00"),
   * daraus kommen Trennzeichen, Nachkommastellen und Symbol.
   */
  function formatMoney(cents, sample) {
    const match = sample && sample.match(/1(\D?)000(?:(\D)(\d+))?/);
    if (!match) return null;

    const [whole, thousands, decimal, decimals] = match;
    const [integer, fraction] = (cents / 100).toFixed(decimals ? decimals.length : 0).split('.');
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return sample.replace(whole, fraction ? grouped + decimal + fraction : grouped);
  }

  class ShippingEstimate extends HTMLElement {
    connectedCallback() {
      const { key, country } = this.dataset;
      if (!key || !country) return;

      // Den Liquid-Text sichern, bevor show() ihn ueberschreibt.
      if (this.fallbackText === undefined) {
        this.fallbackText = this.textContent.trim();
        this.fallbackFree = this.classList.contains('cart-totals__shipping--free');
      }

      const cached = readCache(key);
      if (cached !== undefined) {
        this.show(cached);
        return;
      }

      const previous = lastShown.get(country);
      if (previous !== undefined) {
        this.show(previous);
        this.setAttribute('data-loading', '');
      }

      cheapestRate(key, country, this.dataset.currency).then((cents) => {
        if (!this.isConnected) return;
        this.removeAttribute('data-loading');
        if (cents === null) {
          // Zurueck auf den Liquid-Text, falls oben der alte Betrag stand.
          // Ohne Rate (andere Laender ohne Muster-PLZ) bleibt er einfach stehen.
          if (previous !== undefined) this.reset();
          return;
        }
        this.show(cents);
      });
    }

    show(cents) {
      const country = this.dataset.country;

      if (cents === 0) {
        this.classList.add('cart-totals__shipping--free');
        this.textContent = this.dataset.freeText;
        lastShown.set(country, cents);
        return;
      }

      /* Test ab 18.10.2026 (7 Tage): unter der Gratisgrenze keinen Betrag zeigen, nur den Liquid-Text. Zum Beenden HIDE_AMOUNT auf false setzen. */ const HIDE_AMOUNT = true; if (HIDE_AMOUNT) { this.classList.remove('cart-totals__shipping--free'); this.textContent = this.fallbackFree ? 'Wird im Checkout berechnet' : this.fallbackText; lastShown.set(country, cents); return; } const amount = formatMoney(cents, this.dataset.moneySample);
      if (!amount) return;

      this.classList.remove('cart-totals__shipping--free');
      this.innerHTML = `<price-money><bdi>${amount}</bdi></price-money>`;
      lastShown.set(country, cents);
    }

    reset() {
      this.classList.toggle('cart-totals__shipping--free', this.fallbackFree);
      this.textContent = this.fallbackText;
    }
  }

  if (!customElements.get('shipping-estimate')) {
    customElements.define('shipping-estimate', ShippingEstimate);
  }
})();
